/**
 * Everything derived from the raw items: crosswalk status, the overview numbers, and the
 * completion trend. Pure functions over the loaded array -- no fetching, no state.
 */
import {
  EFFORT_WEIGHTS,
  type HipaaActivityEntry,
  type HipaaItem,
  type HipaaPriority,
  type HipaaStatus,
} from '../types/hipaaTypes';

/** The kinds that represent real work. Crosswalk rows are a view of these, not extra work. */
const SCOPED_KINDS = new Set(['checklist', 'errand', 'vendor']);

export const isCounted = (item: HipaaItem): boolean =>
  SCOPED_KINDS.has(item.kind) && item.status !== 'na';

export const isOpen = (status: HipaaStatus): boolean => status !== 'done' && status !== 'na';

/** How much a status counts toward completion. Partial and in-progress are worth half. */
const COMPLETION_VALUE: Record<HipaaStatus, number> = {
  not_started: 0,
  in_progress: 0.5,
  partial: 0.5,
  done: 1,
  na: 0,
};

/** P0 blocks the first BAA, so it dominates any single progress number. */
const PRIORITY_WEIGHT: Record<HipaaPriority, number> = { P0: 3, P1: 2, P2: 1 };

/**
 * A trust-list row's status, rolled up from the sections it maps to.
 *
 * Pinning a row (`statusIsExplicit`) wins; otherwise it follows the checklist items in its
 * sections, so working the checklist moves the trust-list board with no extra bookkeeping.
 */
export const deriveCrosswalkStatus = (row: HipaaItem, all: HipaaItem[]): HipaaStatus => {
  if (row.statusIsExplicit) return row.status;
  if (row.mapsTo.length === 0) return row.status;

  const mapped = all.filter(
    (item) =>
      item.kind === 'checklist' &&
      item.status !== 'na' &&
      row.mapsTo.some((ref) => item.section === ref || item.parentSection === ref),
  );

  if (mapped.length === 0) return 'not_started';
  if (mapped.every((item) => item.status === 'done')) return 'done';
  if (mapped.some((item) => item.status !== 'not_started')) return 'partial';
  return 'not_started';
};

/** The status to display for any item, crosswalk derivation included. */
export const effectiveStatus = (item: HipaaItem, all: HipaaItem[]): HipaaStatus =>
  item.kind === 'crosswalk' ? deriveCrosswalkStatus(item, all) : item.status;

export interface StatusBreakdown {
  not_started: number;
  in_progress: number;
  partial: number;
  done: number;
  na: number;
  total: number;
}

export const emptyBreakdown = (): StatusBreakdown => ({
  not_started: 0,
  in_progress: 0,
  partial: 0,
  done: 0,
  na: 0,
  total: 0,
});

export const breakdownOf = (items: HipaaItem[], all: HipaaItem[]): StatusBreakdown =>
  items.reduce((acc, item) => {
    acc[effectiveStatus(item, all)]++;
    acc.total++;
    return acc;
  }, emptyBreakdown());

export interface PriorityProgress {
  priority: HipaaPriority;
  done: number;
  open: number;
  total: number;
  breakdown: StatusBreakdown;
}

export interface SectionProgress {
  section: string;
  title: string;
  breakdown: StatusBreakdown;
  openP0: number;
}

export interface HipaaStats {
  /** % of P0 items done. The headline: how close we are to being able to sign a BAA. */
  baaReadiness: number;
  p0Done: number;
  p0Total: number;
  openP0: HipaaItem[];

  /** done = 1, partial/in-progress = 0.5, weighted P0x3 / P1x2 / P2x1. */
  weightedCompletion: number;

  overall: StatusBreakdown;
  byPriority: PriorityProgress[];
  bySection: SectionProgress[];

  blocked: HipaaItem[];
  /** Still needing an answer from a console or a vendor. */
  openVerification: number;
  verificationTotal: number;

  vendorsWithoutBaa: number;
  vendorsTotal: number;

  requiredDone: number;
  requiredTotal: number;
  addressableDone: number;
  addressableTotal: number;

  trustListDone: number;
  trustListTotal: number;

  /** Weighted sum over open items that have an effort set. */
  remainingEffort: number;
  /** Open items nobody has sized yet -- the effort number understates by this many. */
  unsized: number;
}

const percent = (done: number, total: number): number =>
  total === 0 ? 0 : Math.round((done / total) * 100);

export const computeStats = (items: HipaaItem[]): HipaaStats => {
  const scoped = items.filter(isCounted);

  const p0 = scoped.filter((item) => item.priorities.includes('P0'));
  const p0Done = p0.filter((item) => item.status === 'done').length;
  const openP0 = p0
    .filter((item) => isOpen(item.status))
    .sort((a, b) => a.order - b.order);

  let weightSum = 0;
  let weightedDone = 0;
  for (const item of scoped) {
    // Unprioritised items record a control already in place; they are not work to schedule.
    const weight = item.priority ? PRIORITY_WEIGHT[item.priority] : 0;
    if (weight === 0) continue;
    weightSum += weight;
    weightedDone += weight * COMPLETION_VALUE[item.status];
  }

  const byPriority: PriorityProgress[] = (['P0', 'P1', 'P2'] as HipaaPriority[]).map((priority) => {
    const group = scoped.filter((item) => item.priorities.includes(priority));
    const done = group.filter((item) => item.status === 'done').length;
    return {
      priority,
      done,
      open: group.length - done,
      total: group.length,
      breakdown: breakdownOf(group, items),
    };
  });

  const sections = [...new Set(scoped.map((item) => item.parentSection))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
  const bySection: SectionProgress[] = sections.map((section) => {
    const group = scoped.filter((item) => item.parentSection === section);
    return {
      section,
      title: group[0]?.parentSectionTitle ?? section,
      breakdown: breakdownOf(group, items),
      openP0: group.filter((item) => item.priorities.includes('P0') && isOpen(item.status)).length,
    };
  });

  const verification = scoped.filter((item) => item.needsVerification);
  const vendors = items.filter((item) => item.kind === 'vendor' && item.status !== 'na');
  const required = scoped.filter((item) => item.specs.includes('R'));
  const addressable = scoped.filter((item) => item.specs.includes('A'));

  const trustList = items.filter((item) => item.kind === 'crosswalk' && item.status !== 'na');
  const trustListDone = trustList.filter(
    (item) => deriveCrosswalkStatus(item, items) === 'done',
  ).length;

  const open = scoped.filter((item) => isOpen(item.status));
  const remainingEffort = open.reduce(
    (sum, item) => sum + (item.effort ? EFFORT_WEIGHTS[item.effort] : 0),
    0,
  );

  return {
    baaReadiness: percent(p0Done, p0.length),
    p0Done,
    p0Total: p0.length,
    openP0,

    weightedCompletion: percent(weightedDone, weightSum),

    overall: breakdownOf(scoped, items),
    byPriority,
    bySection,

    blocked: scoped.filter((item) => item.blocked).sort((a, b) => a.order - b.order),
    openVerification: verification.filter((item) => isOpen(item.status)).length,
    verificationTotal: verification.length,

    vendorsWithoutBaa: vendors.filter((item) => item.status !== 'done').length,
    vendorsTotal: vendors.length,

    requiredDone: required.filter((item) => item.status === 'done').length,
    requiredTotal: required.length,
    addressableDone: addressable.filter((item) => item.status === 'done').length,
    addressableTotal: addressable.length,

    trustListDone,
    trustListTotal: trustList.length,

    remainingEffort,
    unsized: open.filter((item) => !item.effort).length,
  };
};

export interface TrendBucket {
  /** Monday of the week, ISO date. */
  weekStart: string;
  label: string;
  completed: number;
}

const mondayOf = (date: Date): Date => {
  const copy = new Date(date);
  // getDay() is 0 on Sunday, which belongs to the week that started six days earlier.
  const offset = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - offset);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * Items completed per week, from the activity feed.
 *
 * Empty until history accrues -- it only starts counting once people are working in the
 * tracker, which is the point of keeping history at all.
 *
 * @param weeks - How many weeks back to show, including the current one.
 */
export const completionTrend = (activity: HipaaActivityEntry[], weeks = 8): TrendBucket[] => {
  const buckets: TrendBucket[] = [];
  const thisMonday = mondayOf(new Date());

  for (let index = weeks - 1; index >= 0; index--) {
    const start = new Date(thisMonday);
    start.setDate(start.getDate() - index * 7);
    buckets.push({
      weekStart: start.toISOString().slice(0, 10),
      label: start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      completed: 0,
    });
  }

  const byWeek = new Map(buckets.map((bucket) => [bucket.weekStart, bucket]));

  for (const entry of activity) {
    if (entry.type !== 'status' || entry.to !== 'done' || !entry.at) continue;
    const bucket = byWeek.get(mondayOf(new Date(entry.at)).toISOString().slice(0, 10));
    if (bucket) bucket.completed++;
  }

  return buckets;
};
